<template>
  <div class="station-analysis-panel">
    <div class="analysis-content">
      <section class="overview-section alarm-detail-section">
        <h4>告警详情</h4>
        <AlarmRecordTable :records="props.alarms" :show-station="false" pagination prioritize-alarm-columns
          :alarm-code-linkable="hasDiagnosisForAlarmCode" @alarm-code-click="focusDiagnosisByAlarmCode" />
      </section>

      <section class="summary-section">
        <div class="section-title-row">
          <h4>智能诊断</h4>
          <Transition name="phase-status" mode="out-in">
            <span :key="phase" class="phase-label" :class="`phase-${phase}`">
              <span class="phase-dot" aria-hidden="true"></span>
              {{ phaseText }}
            </span>
          </Transition>
        </div>

        <div v-if="!diagnosis" class="diagnosis-section diagnosis-process-section">
          <div v-if="showProgress" class="process-loading" aria-live="polite">
            <div v-if="processTitle !== '分析过程'" class="process-title">{{ processTitle }}</div>
            <ol class="process-steps-live">
              <li v-for="step in processSteps" :key="step.id" :class="`step-${step.status}`">
                <span class="step-icon">
                  <span v-if="step.status === 'completed'" class="icon-check">✓</span>
                  <span v-else-if="step.status === 'failed'" class="icon-failed">!</span>
                  <span v-else-if="step.status === 'started'" class="icon-loading"></span>
                  <span v-else class="icon-pending">•</span>
                </span>
                <span class="step-text">{{ step.text }}</span>
                <span v-if="step.statusDisplay" class="step-status">{{ step.statusDisplay }}</span>
              </li>
            </ol>
            <div v-if="toolItems.length" class="tool-list">
              <div class="tool-list-title">
                <span>辅助工具</span>
                <span>{{ completedToolCount }}/{{ toolItems.length }} 项完成</span>
              </div>
              <div class="tool-items">
                <div v-for="tool in toolItems" :key="tool.id" class="tool-item">
                  <v-icon size="15" :class="{ 'tool-icon-loading': tool.status === 'started' }"
                    :color="tool.status === 'failed' ? '#F5222D' : tool.status === 'completed' ? '#52C41A' : '#3A68FF'">
                    {{ toolStatusIcon(tool.status) }}
                  </v-icon>
                  <span class="tool-copy">
                    <span class="tool-name">{{ tool.label }}</span>
                    <span class="tool-description" :class="{ 'tool-error-text': tool.error }">
                      {{ tool.error || tool.description }}
                    </span>
                  </span>
                  <span class="tool-status" :class="`tool-${tool.status}`">{{ toolStatusText(tool.status) }}</span>
                </div>
              </div>
            </div>
          </div>

          <div v-if="errorMessage && !diagnosis" class="error-state" role="alert">
            <span class="error-icon">⚠️</span>
            <p>{{ errorMessage }}</p>
            <ul v-if="errorDetails.length" class="error-details">
              <li v-for="item in errorDetails" :key="item">{{ item }}</li>
            </ul>
            <v-btn color="primary" variant="flat" size="small" @click="startAnalysis">重新分析</v-btn>
          </div>

          <div v-if="connectionWarning" class="status-warning" role="status">
            {{ connectionWarning }}
            <v-btn variant="text" color="primary" size="small" @click="startAnalysis">重新分析</v-btn>
          </div>
        </div>

        <div v-if="diagnosis" class="diagnosis-section diagnosis-overview-section">
          <div class="risk-overview" :class="`risk-${diagnosis.riskLevel}`">
            <div class="risk-heading">
              <!-- <span class="risk-badge">{{ riskLevelText(diagnosis.riskLevel) }}</span> -->
              <span v-if="analysisId" class="analysis-id">分析编号：{{ analysisId }}</span>
            </div>
            <p>{{ diagnosis.stationSummary || answer || '暂无电站风险摘要' }}</p>
          </div>
        </div>

        <div v-if="diagnosis" class="diagnosis-section diagnosis-result-section">
          <div v-if="orderedDiagnoses.length" class="diagnosis-result-layout">
            <aside class="diagnosis-directory" aria-label="告警目录">
              <!-- <div class="directory-title">告警目录</div> -->
              <nav>
                <button v-for="(item, index) in orderedDiagnoses" :key="`directory-${item.alarmCode}`" type="button"
                  :class="{ active: activeDiagnosisCode === item.alarmCode }"
                  @click="scrollToDiagnosis(item.alarmCode)">
                  <span class="directory-index">{{ index + 1 }}</span>
                  <span class="directory-text">
                    <span class="directory-heading">
                      <strong>{{ item.alarmName || '未命名告警' }}</strong>
                    </span>
                    <small class="directory-code" :class="getCategoryClass(item.category)" :title="item.alarmCode">
                      <span class="directory-code-dot" aria-hidden="true"></span>
                      <span class="directory-code-text">{{ item.alarmCode }}</span>
                    </small>
                  </span>
                </button>
              </nav>
            </aside>

            <div class="diagnosis-list">
              <article v-for="item in orderedDiagnoses" :id="diagnosisAnchorId(item.alarmCode)" :key="item.alarmCode"
                class="diagnosis-card"
                :class="[getCategoryClass(item.category), { 'is-collapsed': !isDiagnosisExpanded(item.alarmCode) }]">
                <button type="button" class="diagnosis-card-header" :aria-expanded="isDiagnosisExpanded(item.alarmCode)"
                  @click="toggleDiagnosis(item.alarmCode)">
                  <div class="diagnosis-card-title">
                    <h5>{{ item.alarmName || '未命名告警' }}</h5>
                    <span>{{ item.alarmCode }}</span>
                  </div>
                  <div class="diagnosis-card-actions">
                    <span class="diagnosis-category-badge" :class="getCategoryClass(item.category)">
                      <v-icon size="13">{{ getCategoryIcon(item.category) }}</v-icon>
                      {{ getCategoryLabel(item.category) }}
                    </span>
                    <v-icon size="18" color="rgba(46, 46, 46, 0.45)">
                      {{ isDiagnosisExpanded(item.alarmCode) ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                    </v-icon>
                  </div>
                </button>

                <v-expand-transition>
                  <div v-show="isDiagnosisExpanded(item.alarmCode)" class="diagnosis-card-body">
                    <div class="diagnosis-flow">
                      <div class="diagnosis-flow-step">
                        <span class="flow-step-index">1</span>
                        <div class="flow-step-content">
                          <h6>告警概况</h6>
                          <ul v-if="item.confirmedFacts?.length">
                            <li v-for="fact in item.confirmedFacts" :key="fact">{{ fact }}</li>
                          </ul>
                          <p v-else>暂无对应内容</p>
                        </div>
                      </div>
                      <div class="diagnosis-flow-step">
                        <span class="flow-step-index">2</span>
                        <div class="flow-step-content">
                          <h6>设备与版本证据</h6>
                          <ul v-if="deviceEvidence(item).length" class="device-evidence-list">
                            <li v-for="device in deviceEvidence(item)" :key="device.uuid" class="device-evidence-item">
                              <v-icon size="13" color="#3A68FF">mdi-devices</v-icon>
                              <span class="device-evidence-name">{{ device.name || device.uuid }}</span>
                              <span v-if="device.name" class="device-evidence-uuid">{{ device.uuid }}</span>
                              <span class="device-evidence-version" :class="{ 'is-missing': !device.version }">
                                {{ device.version || '暂无版本信息' }}
                              </span>
                            </li>
                          </ul>
                          <p v-else>暂无对应内容</p>
                        </div>
                      </div>
                      <div class="diagnosis-flow-step">
                        <span class="flow-step-index">3</span>
                        <div class="flow-step-content">
                          <h6>原因分析</h6>
                          <ul v-if="item.possibleCauses?.length">
                            <li v-for="cause in item.possibleCauses" :key="cause.cause">
                              {{ cause.cause }}
                              <!-- <span v-for="ref in cause.evidenceRefs || []" :key="ref" class="evidence-tag">{{ ref }}</span> -->
                            </li>
                          </ul>
                          <p v-else>暂无对应内容</p>
                        </div>
                      </div>
                      <div class="diagnosis-flow-step">
                        <span class="flow-step-index">4</span>
                        <div class="flow-step-content">
                          <h6>影响评估</h6>
                          <ul v-if="item.impact?.length">
                            <li v-for="impact in item.impact" :key="impact">{{ impact }}</li>
                          </ul>
                          <p v-else>暂无对应内容</p>
                        </div>
                      </div>
                      <div class="diagnosis-flow-step">
                        <span class="flow-step-index">5</span>
                        <div class="flow-step-content">
                          <h6>处置建议</h6>
                          <ul v-if="item.operationSteps?.length">
                            <li v-for="step in item.operationSteps" :key="step">{{ step }}</li>
                          </ul>
                          <p v-else>暂无对应内容</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </v-expand-transition>
              </article>
            </div>
          </div>
          <div v-else class="empty-inline">暂无智能诊断结果</div>
        </div>
      </section>
    </div>

    <!-- 暂时隐藏入口，保留后续恢复能力 -->
    <div v-if="diagnosis && !isLoading" v-show="false" class="analysis-footer">
      <v-btn color="primary" variant="flat" @click="jumpToFullAgent">
        继续在智能体中深度分析 →
      </v-btn>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, nextTick, onMounted, onBeforeUnmount } from 'vue'
import { useRouter } from 'vue-router'
import { getStationAnalysisContext } from '@/api/alarmDailyReport'
import { consumeAlarmDiagnosis } from '@/api/alarmDiagnosis'
import AlarmRecordTable from './AlarmReportDrawer/AlarmRecordTable.vue'
import { getCategoryClass, getCategoryIcon, getCategoryLabel } from './AlarmReportDrawer/category'

const router = useRouter()

const props = defineProps({
  reportId: {
    type: Number,
    default: null
  },
  stationId: {
    type: [String, Number],
    default: null
  },
  stationName: {
    type: String,
    default: ''
  },
  stationLevel: {
    type: String,
    default: ''
  },
  alarms: {
    type: Array,
    default: () => []
  }
})

// 电站概览：从传入的告警列表直接计算，不走 mock/接口
const overview = computed(() => {
  const list = props.alarms || []
  const unrecovered = list.filter(item => item.state === '未恢复')
  const latestTriggerTime = unrecovered
    .map(item => item.lastTriggerTime || item.triggerTime)
    .filter(Boolean)
    .sort((a, b) => String(b).localeCompare(String(a)))[0] || ''

  return {
    total: list.length,
    triggerCount: list.reduce((sum, item) => sum + Number(item.triggerCount || 1), 0),
    safety: unrecovered.filter(item => item.category === 'SAFETY').length,
    income: unrecovered.filter(item => item.category === 'INCOME').length,
    normal: unrecovered.filter(item => item.category === 'NORMAL').length,
    unrecovered: unrecovered.length,
    recovered: list.filter(item => item.state === '已恢复').length,
    autoRecovered: list.filter(item => item.state === '已自动恢复').length,
    deviceCount: new Set(unrecovered.map(item => item.equipmentId || item.equipmentName).filter(Boolean)).size,
    latestTriggerTime
  }
})

const analysisContext = ref(null)
const DEFAULT_STEPS = [
  { id: 'validate_context', text: '接收告警信息', status: 'pending' },
  { id: 'gather_evidence', text: '整理诊断依据', status: 'pending' },
  { id: 'llm_diagnosis', text: '生成诊断建议', status: 'pending' }
]

const phase = ref('idle')
const errorMessage = ref('')
const errorDetails = ref([])
const connectionWarning = ref('')
const diagnosis = ref(null)
const answer = ref('')
const missingEvidence = ref([])
const analysisId = ref('')
const contextId = ref('')
const elapsedMs = ref(null)
const processSteps = ref(DEFAULT_STEPS.map(step => ({ ...step })))
const tools = ref({})
const expandedDiagnosisCodes = ref([])
const activeDiagnosisCode = ref('')
let controller = null
let runSequence = 0
let receivedDone = false
let receivedTerminal = false

const isLoading = computed(() => ['loading-context', 'connecting', 'running'].includes(phase.value))
const loadingText = computed(() => phase.value === 'loading-context' ? '正在获取电站告警分析上下文...' : '告警诊断智能体正在分析...')
const showProgress = computed(() => phase.value !== 'idle')
const processTitle = computed(() => isLoading.value ? loadingText.value : '分析过程')
const phaseText = computed(() => ({
  idle: '等待分析',
  'loading-context': '准备分析',
  connecting: '连接分析服务',
  running: '正在分析',
  success: '分析完成',
  failed: '分析失败'
}[phase.value] || '分析中'))
const toolItems = computed(() => Object.values(tools.value))
const completedToolCount = computed(() => toolItems.value.filter(tool => tool.status === 'completed').length)
const orderedDiagnoses = computed(() => {
  const result = diagnosis.value
  if (!result) return []
  const list = Array.isArray(result.alarmDiagnoses) ? result.alarmDiagnoses : []
  const byCode = new Map(list.map(item => [item.alarmCode, item]))
  const ordered = (result.recommendedOrder || []).map(code => byCode.get(code)).filter(Boolean)
  const used = new Set(ordered.map(item => item.alarmCode))
  return [...ordered, ...list.filter(item => !used.has(item.alarmCode))]
})

function resetAnalysisState() {
  analysisContext.value = null
  phase.value = 'loading-context'
  errorMessage.value = ''
  errorDetails.value = []
  connectionWarning.value = ''
  diagnosis.value = null
  answer.value = ''
  missingEvidence.value = []
  analysisId.value = ''
  contextId.value = ''
  elapsedMs.value = null
  processSteps.value = DEFAULT_STEPS.map(step => ({ ...step }))
  tools.value = {}
  expandedDiagnosisCodes.value = []
  activeDiagnosisCode.value = ''
  receivedDone = false
  receivedTerminal = false
}

async function startAnalysis() {
  controller?.abort()
  controller = new AbortController()
  const currentRun = ++runSequence
  resetAnalysisState()

  try {
    if (!props.reportId || !props.stationId) throw new Error('缺少日报ID或电站ID，无法开始分析')
    const response = await getStationAnalysisContext({
      reportId: props.reportId,
      stationId: props.stationId
    })
    if (currentRun !== runSequence) return

    const context = response?.data
    if (!context || typeof context !== 'object') throw new Error('后端未返回有效的电站分析上下文')
    analysisContext.value = context
    contextId.value = context.contextId || ''
    phase.value = 'connecting'

    await consumeAlarmDiagnosis(context, handleDiagnosisEvent, controller.signal)
    if (currentRun !== runSequence) return

    if (!receivedTerminal) {
      phase.value = 'failed'
      errorMessage.value = '连接已结束，但未收到分析结果，请重新分析'
    } else if (!receivedDone) {
      connectionWarning.value = '已收到诊断结果，但连接未正常结束，结果可能不完整。'
    }
  } catch (error) {
    if (error?.name === 'CanceledError' || error?.name === 'AbortError' || currentRun !== runSequence) return
    if (diagnosis.value) {
      connectionWarning.value = '已收到诊断结果，但连接意外中断，结果可能不完整。'
      return
    }
    if (receivedTerminal && phase.value === 'failed') return
    console.error('获取电站智能分析失败:', error)
    phase.value = 'failed'
    errorMessage.value = error?.message || '分析服务暂时不可用，请稍后重试'
  }
}

function handleDiagnosisEvent(event) {
  switch (event.type) {
    case 'analysis_started':
      phase.value = 'running'
      analysisId.value = event.analysisId || ''
      contextId.value = event.contextId || contextId.value
      break
    case 'step_detail':
      phase.value = 'running'
      updateStep(event)
      break
    case 'tool_start':
      phase.value = 'running'
      updateTool(event, 'started')
      markEvidenceStepStarted()
      break
    case 'tool_complete':
      updateTool(event, event.success === false ? 'failed' : 'completed')
      break
    case 'tool_error':
      updateTool(event, 'failed')
      break
    case 'analysis_completed':
      receivedTerminal = true
      diagnosis.value = event.diagnosis || null
      expandedDiagnosisCodes.value = (event.diagnosis?.alarmDiagnoses || []).map(item => item.alarmCode)
      activeDiagnosisCode.value = getFirstDiagnosisCode(event.diagnosis)
      answer.value = event.answer || ''
      missingEvidence.value = Array.isArray(event.missingEvidence) ? event.missingEvidence : []
      analysisId.value = event.analysisId || analysisId.value
      contextId.value = event.contextId || contextId.value
      elapsedMs.value = event.elapsedMs ?? elapsedMs.value
      phase.value = 'success'
      completePendingSteps()
      break
    case 'analysis_failed':
      receivedTerminal = true
      phase.value = 'failed'
      analysisId.value = event.analysisId || analysisId.value
      errorMessage.value = event.message || '告警智能分析失败'
      errorDetails.value = Array.isArray(event.errors) ? event.errors : []
      break
    case 'done':
      receivedDone = true
      elapsedMs.value = event.total_elapsed_ms ?? elapsedMs.value
      break
    default:
      if (import.meta.env.DEV) console.debug('忽略未知告警诊断事件', event)
  }
}

function isDiagnosisExpanded(alarmCode) {
  return expandedDiagnosisCodes.value.includes(alarmCode)
}

function toggleDiagnosis(alarmCode) {
  activeDiagnosisCode.value = alarmCode
  expandedDiagnosisCodes.value = isDiagnosisExpanded(alarmCode)
    ? expandedDiagnosisCodes.value.filter(code => code !== alarmCode)
    : [...expandedDiagnosisCodes.value, alarmCode]
}

function getFirstDiagnosisCode(result) {
  const diagnoses = result?.alarmDiagnoses || []
  const diagnosisCodes = new Set(diagnoses.map(item => item.alarmCode))
  return (result?.recommendedOrder || []).find(code => diagnosisCodes.has(code)) || diagnoses[0]?.alarmCode || ''
}

function diagnosisAnchorId(alarmCode) {
  const stationKey = String(props.stationId || 'station').replace(/[^a-zA-Z0-9_-]/g, '-')
  const alarmKey = String(alarmCode || 'alarm').replace(/[^a-zA-Z0-9_-]/g, '-')
  return `station-diagnosis-${stationKey}-${alarmKey}`
}

function resolveDiagnosisCode(alarmCode) {
  const sourceCode = String(alarmCode || '')
  if (!sourceCode) return ''

  const exactMatch = orderedDiagnoses.value.find(item => String(item.alarmCode) === sourceCode)
  return exactMatch?.alarmCode || ''
}

function hasDiagnosisForAlarmCode(alarmCode) {
  return Boolean(resolveDiagnosisCode(alarmCode))
}

function focusDiagnosisByAlarmCode(alarmCode) {
  const diagnosisCode = resolveDiagnosisCode(alarmCode)
  if (diagnosisCode) scrollToDiagnosis(diagnosisCode)
}

function scrollToDiagnosis(alarmCode) {
  activeDiagnosisCode.value = alarmCode
  if (!isDiagnosisExpanded(alarmCode)) {
    expandedDiagnosisCodes.value = [...expandedDiagnosisCodes.value, alarmCode]
  }
  nextTick(() => {
    document.getElementById(diagnosisAnchorId(alarmCode))?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    })
  })
}

function updateStep(event) {
  let index = processSteps.value.findIndex(step => step.id === event.step_id || step.text === event.step)
  if (index < 0) {
    index = event.step?.includes('依据') ? 1 : -1
  }
  if (index < 0) {
    processSteps.value.push({ id: event.step_id || event.step, text: event.step || '分析步骤', status: event.status || 'started' })
    index = processSteps.value.length - 1
  }
  processSteps.value[index] = {
    ...processSteps.value[index],
    id: event.step_id || processSteps.value[index].id,
    text: event.step || processSteps.value[index].text,
    status: event.status || 'started',
    statusDisplay: event.status_display || ''
  }
}

function updateTool(event, status) {
  const id = event.tool_id || event.tool_name || `tool-${Object.keys(tools.value).length + 1}`
  tools.value = {
    ...tools.value,
    [id]: {
      id,
      label: toolNameText(event.tool_name || id),
      description: toolDescriptionText(event.tool_name || id),
      status,
      error: event.error || ''
    }
  }
}

function markEvidenceStepStarted() {
  if (processSteps.value[1]?.status === 'pending') {
    processSteps.value[1] = { ...processSteps.value[1], status: 'started' }
  }
}

function completePendingSteps() {
  processSteps.value = processSteps.value.map(step => step.status === 'failed' ? step : { ...step, status: 'completed' })
}

function toolNameText(id) {
  const key = String(id || '')
  if (key.includes('query_alarm_sop') || key.includes('alarm_sop')) return '查询处理流程'
  if (key.includes('query_alarm_code_analysis') || key.includes('code_analysis')) return '查询代码分析'
  if (/[\u4e00-\u9fff]/.test(key)) return key
  return '补充诊断依据'
}

function toolDescriptionText(id) {
  const key = String(id || '')
  if (key.includes('query_alarm_sop') || key.includes('alarm_sop')) return '检索匹配告警的标准处置流程'
  if (key.includes('query_alarm_code_analysis') || key.includes('code_analysis')) return '检索告警码对应的机制与原因分析'
  return '检索本次诊断所需的辅助信息'
}

function toolStatusText(status) {
  return status === 'completed' ? '已完成' : status === 'failed' ? '失败' : '查询中'
}

function toolStatusIcon(status) {
  if (status === 'completed') return 'mdi-check-circle-outline'
  if (status === 'failed') return 'mdi-alert-circle-outline'
  return 'mdi-loading'
}

function riskLevelText(level) {
  return level === 'high' ? '高风险' : level === 'medium' ? '中风险' : '低风险'
}

// 设备与版本证据：读取诊断项的 deviceSoftwareVersions（key=equipmentUuid）。
// 仅展示后端透传证据：优先诊断结果中随附的映射；为空时回退到 Java 分析上下文中同告警码的映射。
// 后端未提供任何版本映射时显示「暂无对应内容」，不根据告警列表自行造证据。
function deviceEvidence(item) {
  if (item.deviceSoftwareVersions && Object.keys(item.deviceSoftwareVersions).length) {
    return Object.entries(item.deviceSoftwareVersions).map(([uuid, version]) => ({
      uuid,
      name: '',
      version: String(version ?? ''),
    }))
  }
  const fallbackGroup = (analysisContext.value?.alarmGroups || []).find(g => String(g.alarmCode) === String(item.alarmCode))
  const fallbackVersions = fallbackGroup?.deviceSoftwareVersions
  if (fallbackVersions && Object.keys(fallbackVersions).length) {
    return Object.entries(fallbackVersions).map(([uuid, version]) => ({
      uuid,
      name: '',
      version: String(version ?? ''),
    }))
  }
  return []
}

onMounted(startAnalysis)
onBeforeUnmount(() => {
  runSequence += 1
  controller?.abort()
})

// 跳转到完整智能体页面，携带电站级上下文
function jumpToFullAgent() {
  const context = {
    source: 'alarm_report',
    intent: 'station.deep_analysis',
    context: {
      station_id: props.stationId,
      station_name: props.stationName,
      station_level: props.stationLevel,
      alarm_count: overview.value.total,
      trigger_count: overview.value.triggerCount,
      safety_count: overview.value.safety,
      income_count: overview.value.income,
      normal_count: overview.value.normal,
      unrecovered_count: overview.value.unrecovered,
      recovered_count: overview.value.recovered + overview.value.autoRecovered,
      quick_analysis: diagnosis.value
    }
  }

  const aiUrl = router.resolve({
    path: '/ai',
    query: {
      stationId: props.stationId,
      context: encodeURIComponent(JSON.stringify(context))
    }
  })

  window.open(aiUrl.href, '_blank')
}
</script>

<style scoped lang="scss">
.station-analysis-panel {
  min-height: 100%;
  background: transparent;
  color: #2e2e2e;
  font-family: 'PingFang SC', 'HarmonyOS Sans SC', sans-serif;
}

:deep(.alarm-code-cell) {
  font-weight: 600;
}

.analysis-content {
  padding: 0 24px 24px;

  section {
    padding: 24px 0;
    margin-bottom: 0;
    border: none;
    border-radius: 0;
    background: transparent;
    box-shadow: none;
    border-bottom: none;

    &:last-child {
      border-bottom: none;
    }

    h4 {
      font-size: 16px;
      font-weight: 600;
      color: #2e2e2e;
      margin: 0 0 20px 0;
      padding-bottom: 0;
      border-bottom: none;
      display: flex;
      align-items: center;
      gap: 8px;

      // &::before {
      //   content: '';
      //   display: inline-block;
      //   width: 3px;
      //   height: 16px;
      //   border-radius: 2px;
      //   background: #3A68FF;
      //   flex-shrink: 0;
      // }
    }
  }
}

.analysis-content .overview-section {
  padding-bottom: 12px;
}

.analysis-content .summary-section {
  padding-top: 14px;
}

.analysis-footer {
  display: flex;
  justify-content: center;
  padding: 0 24px 24px;

  :deep(.v-btn) {
    border-radius: 999px;
    padding: 6px 20px;
    font-size: 14px;
    font-weight: 500;
    text-transform: none;
    letter-spacing: 0;
    box-shadow: 0 2px 8px rgba(58, 104, 255, 0.2);
    transition: all 0.2s;

    &:hover {
      box-shadow: 0 4px 12px rgba(58, 104, 255, 0.3);
      transform: translateY(-1px);
    }
  }
}

.overview-section {
  .analysis-tabs {
    display: inline-flex;
    gap: 4px;
    padding: 3px;
    margin-bottom: 16px;
    border-radius: 999px;
    background: #F3F6FF;
    border: 1px solid rgba(58, 104, 255, 0.06);

    button {
      min-width: 72px;
      height: 32px;
      padding: 6px 18px;
      border: 0;
      border-radius: 999px;
      background: transparent;
      color: rgba(46, 46, 46, 0.6);
      font-size: 13px;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.2s;

      &:hover {
        color: #2e2e2e;
        background: rgba(58, 104, 255, 0.04);
      }

      &.active {
        background: #3A68FF;
        color: #fff;
        font-weight: 600;
        box-shadow: 0 2px 8px rgba(58, 104, 255, 0.3);
      }
    }
  }

  .tab-panel {
    min-height: 0;
  }

  .nested-section {
    margin-top: 18px;
    margin-bottom: 0;
  }

  .overview-panel {
    padding: 16px 20px;
    border: 1px solid #F2F3F5;
    border-radius: 10px;
    background: #fafbfc88;
    box-shadow: none;
  }

  .overview-fields {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    column-gap: 48px;
    row-gap: 0;
  }

  .field-item {
    display: grid;
    grid-template-columns: 110px minmax(0, 1fr);
    align-items: center;
    gap: 16px;
    min-width: 0;
    min-height: 40px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);

    label {
      display: inline;
      margin-bottom: 0;
      color: rgba(46, 46, 46, 0.6);
      font-size: 13px;
    }

    span {
      color: #2e2e2e;
      font-size: 14px;
      font-weight: 500;
      line-height: 1.5;
    }
  }

  .level-text {
    justify-self: start;
    font-weight: 700 !important;
    font-variant-numeric: tabular-nums;
  }

  .field-item .level-p0 {
    color: rgba(255, 30, 30, 1);
  }

  .field-item .level-p1 {
    color: rgba(245, 158, 11, 1);
  }

  .field-item .level-p2 {
    color: #3A68FF;
  }

  .danger-text {
    // color: rgba(255, 30, 30, 1) !important;
    font-weight: 600 !important;
  }

  .category-strip {
    display: grid;
    grid-template-columns: 110px minmax(0, 1fr);
    align-items: center;
    gap: 16px;
    min-width: 0;
    min-height: 40px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  }

  .category-label {
    color: rgba(46, 46, 46, 0.6);
    font-size: 13px;
  }

  .category-chart {
    width: 60%;
    min-width: 0;
    padding: 8px 0;
    cursor: help;
  }

  .category-bar {
    display: flex;
    height: 8px;
    overflow: hidden;
    border-radius: 999px;
    background: rgba(0, 0, 0, 0.04);
  }

  .bar-seg {
    display: block;
    min-width: 6px;

    &.safety {
      background: rgba(255, 30, 30, 1);
    }

    &.income {
      background: rgba(245, 158, 11, 1);
    }

    &.normal {
      background: #3A68FF;
    }

    &.empty {
      width: 100%;
      background: rgba(0, 0, 0, 0.08);
    }
  }
}

.category-tooltip-lines {
  display: flex;
  flex-direction: column;
  gap: 3px;
  line-height: 1.5;
}

@media (max-width: 900px) {
  .overview-section {
    .overview-fields {
      grid-template-columns: 1fr;
      row-gap: 0;
    }

    .field-item {
      grid-template-columns: 110px minmax(0, 1fr);
    }

    .category-strip {
      width: 100%;
    }
  }
}

.ranking-list {
  display: flex;
  flex-direction: column;
  padding: 4px 0;
}

.rank-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 4px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  transition: background 0.2s;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: rgba(58, 104, 255, 0.02);
  }
}

.rk {
  width: 24px;
  height: 24px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  flex-shrink: 0;
  background: rgba(0, 0, 0, 0.05);
  color: rgba(46, 46, 46, 0.5);
  font-variant-numeric: tabular-nums;

  &.rk-1 {
    background: rgba(255, 30, 30, 1);
    color: #fff;
  }

  &.rk-2 {
    background: rgba(255, 91, 62, 1);
    color: #fff;
  }

  &.rk-3 {
    background: rgba(58, 104, 255, 1);
    color: #fff;
  }
}

.rk-info {
  flex: 1;
  min-width: 0;
}

.rk-name {
  font-size: 13px;
  font-weight: 500;
  color: #2e2e2e;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.rk-bar-wrap {
  height: 5px;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.05);
  margin-top: 6px;
  overflow: hidden;
}

.rk-bar {
  height: 100%;
  border-radius: 999px;
  background: linear-gradient(90deg, #3A68FF 0%, #6B8CFF 100%);
  transition: width 0.8s ease;
}

.rank-item:nth-child(1) .rk-bar {
  background: linear-gradient(90deg, rgba(255, 30, 30, 1) 0%, rgba(255, 90, 90, 1) 100%);
}

.rank-item:nth-child(2) .rk-bar {
  background: linear-gradient(90deg, rgba(255, 91, 62, 1) 0%, rgba(255, 91, 62, 0.7) 100%);
}

.rk-code {
  flex-shrink: 0;
  color: rgba(46, 46, 46, 0.4);
  font-family: 'SF Mono', 'Courier New', monospace;
  font-size: 12px;
  white-space: nowrap;
}

.rk-val {
  flex-shrink: 0;
  min-width: 40px;
  text-align: right;
  color: #2e2e2e;
  font-size: 16px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.rank-item:nth-child(1) .rk-val {
  color: rgba(255, 30, 30, 1);
}

.rank-item:nth-child(2) .rk-val {
  color: rgba(255, 91, 62, 1);
}

.empty-ranking {
  padding: 32px 16px;
  color: rgba(46, 46, 46, 0.45);
  font-size: 13px;
  text-align: center;
}

// 智能摘要
.summary-section {

  .section-title-row {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 8px;

    h4 {
      margin-bottom: 0;
    }
  }

  .phase-label {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 0;
    padding: 4px 10px;
    border-radius: 999px;
    background: rgba(58, 104, 255, 0.08);
    color: #3A68FF;
    font-size: 12px;
    font-weight: 600;
    margin-left: 10px;

    &.phase-success {
      background: rgba(82, 196, 26, 0.1);
      color: #52C41A;
    }

    &.phase-failed {
      background: rgba(245, 34, 45, 0.08);
      color: #F5222D;
    }

    &.phase-loading-context .phase-dot,
    &.phase-connecting .phase-dot,
    &.phase-running .phase-dot {
      animation: phase-dot-pulse 1.35s ease-in-out infinite;
    }
  }

  .loading-state,
  .error-state,
  .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 32px 16px;
    text-align: center;

    p {
      margin: 12px 0 0 0;
      color: rgba(46, 46, 46, 0.6);
      font-size: 13px;
    }
  }

  .spinner {
    width: 32px;
    height: 32px;
    border: 3px solid rgba(58, 104, 255, 0.1);
    border-top: 3px solid #3A68FF;
    border-radius: 50%;
    animation: dialog-spin 1s linear infinite;
  }

  .error-state {
    .error-icon {
      font-size: 32px;
    }

    p {
      color: rgba(255, 30, 30, 1);
    }

    .error-details {
      margin: 10px 0 0;
      padding-left: 20px;
      color: rgba(217, 45, 32, 0.85);
      font-size: 12px;
      text-align: left;
    }

    :deep(.v-btn) {
      margin-top: 16px;
    }
  }
}

.summary-section {
  .diagnosis-section {
    margin-top: 18px;
    overflow: visible;
    border-top: 0;
    border-radius: 0;
    background: transparent;
  }

  .diagnosis-process-section {
    margin-top: 14px;
  }

  .diagnosis-result-section {
    margin-top: 20px;
  }
}

@keyframes dialog-spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

// 分析过程（loading时实时显示）
.process-loading {
  padding: 0 16px 8px;
  background: transparent;

  .process-title {
    margin-bottom: 10px;
    color: rgba(46, 46, 46, 0.52);
    font-size: 12px;
    font-weight: 400;
  }

  .process-steps-live {
    margin: 0;
    padding: 0;
    list-style: none;

    li {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 10px 0;
      font-size: 13px;
      transition: all 0.3s ease;

      .step-icon {
        width: 22px;
        height: 22px;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        border-radius: 50%;
        font-size: 12px;
      }

      .icon-check {
        color: #52C41A;
        font-weight: 700;
      }

      .icon-loading {
        width: 14px;
        height: 14px;
        border: 2px solid #3A68FF;
        border-top-color: transparent;
        border-radius: 50%;
        animation: dialog-spin 0.8s linear infinite;
      }

      .icon-pending {
        color: rgba(46, 46, 46, 0.2);
        font-size: 10px;
      }

      .step-text {
        flex: 1;
      }

      &.step-completed {
        color: #2e2e2e;

        .step-icon {
          background: rgba(82, 196, 26, 0.1);
        }
      }

      &.step-started {
        color: #3A68FF;
        font-weight: 500;

        .step-icon {
          background: rgba(58, 104, 255, 0.08);
        }
      }

      &.step-pending {
        color: rgba(46, 46, 46, 0.4);
      }

      &.step-failed {
        color: #F5222D;

        .step-icon {
          background: rgba(245, 34, 45, 0.08);
        }
      }

      .icon-failed {
        color: #F5222D;
        font-weight: 700;
      }

      .step-status {
        color: rgba(46, 46, 46, 0.45);
        font-size: 12px;
      }
    }
  }

  .tool-list {
    margin-top: 14px;
    padding-top: 0;
    border-top: 0;
  }

  .tool-list-title {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 2px;
    color: rgba(46, 46, 46, 0.45);
    font-size: 11px;
  }

  .tool-items {
    display: flex;
    flex-direction: column;
  }

  .tool-item {
    display: grid;
    grid-template-columns: 16px minmax(0, 1fr) auto;
    align-items: center;
    min-height: 34px;
    gap: 8px;
    padding: 5px 0;
    color: rgba(46, 46, 46, 0.68);
    font-size: 12px;

    &:not(:last-child) {
      border-bottom: 0;
    }
  }

  .tool-copy {
    min-width: 0;
  }

  .tool-name {
    display: block;
    color: #2e2e2e;
    font-weight: 500;
  }

  .tool-description {
    display: block;
    min-width: 0;
    margin-top: 1px;
    overflow: hidden;
    color: rgba(46, 46, 46, 0.48);
    font-size: 11px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .tool-status {
    flex-shrink: 0;
    font-size: 11px;
  }

  .tool-completed {
    color: #52C41A;
  }

  .tool-failed,
  .tool-error-text {
    color: #F5222D;
  }

  .tool-started {
    color: #3A68FF;
  }

  .tool-icon-loading {
    animation: dialog-spin 0.8s linear infinite;
  }
}

.diagnosis-process-section .process-loading {
  padding-top: 12px;
  padding-bottom: 14px;
  border-radius: 6px;
  background: rgba(58, 104, 255, 0.025);
}

.status-warning {
  margin-top: 14px;
  padding: 12px 16px;
  border: 1px solid rgba(255, 145, 0, 0.24);
  border-radius: 8px;
  background: rgba(255, 145, 0, 0.06);
  color: #8A4B08;
  font-size: 13px;
  line-height: 1.7;
}

.risk-overview {
  padding: 16px 18px;
  border: 1px solid rgba(58, 104, 255, 0.12);
  border-left: 3px solid #3A68FF;
  border-radius: 6px;
  background: rgba(58, 104, 255, 0.04);
  margin: 4px 0 14px;

  &.risk-high {
    border-left-color: #F5222D;
    background: rgba(245, 34, 45, 0.04);
  }

  &.risk-medium {
    border-left-color: #FAAD14;
    background: rgba(250, 173, 20, 0.08);
  }

  .risk-heading {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
  }

  .risk-badge {
    font-size: 13px;
    font-weight: 700;
  }

  .analysis-id {
    color: rgba(46, 46, 46, 0.45);
    font-size: 11px;
  }

  p {
    margin: 10px 0 0;
    font-size: 14px;
    line-height: 1.8;
  }
}

.diagnosis-result-layout {
  display: grid;
  grid-template-columns: 180px minmax(0, 1fr);
  align-items: start;
  gap: 18px;
  margin: 0;
  padding: 0 0 18px;
}

.diagnosis-directory {
  position: sticky;
  top: 12px;
  min-width: 0;
  padding-right: 14px;
  border-right: 1px solid rgba(0, 0, 0, 0.06);

  .directory-title {
    margin: 0 8px 8px;
    color: rgba(46, 46, 46, 0.45);
    font-size: 12px;
  }

  nav {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  button {
    display: grid;
    grid-template-columns: 22px minmax(0, 1fr);
    align-items: center;
    width: 100%;
    gap: 7px;
    padding: 7px 8px;
    border: 0;
    border-radius: 6px;
    background: transparent;
    color: inherit;
    font-family: inherit;
    text-align: left;
    cursor: pointer;
    transition: background 0.2s, color 0.2s;

    &:hover {
      background: rgba(58, 104, 255, 0.04);
    }

    &:focus-visible {
      outline: 2px solid rgba(58, 104, 255, 0.4);
      outline-offset: -2px;
    }

    &.active {
      background: rgba(58, 104, 255, 0.07);

      .directory-text strong {
        color: #2e2e2eee;
      }

      .directory-index {
        font-weight: 900;
        color: #2e2e2ebb;
      }

      .directory-code {
        font-weight: 600;
      }
    }
  }

}

.directory-index {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  color: rgba(46, 46, 46, 0.42);
  font-size: 11px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}

.directory-text {
  display: block;
  min-width: 0;

  strong,
  small {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  strong {
    color: rgba(46, 46, 46, 0.76);
    font-size: 12px;
    font-weight: 500;
  }

  small {
    margin-top: 3px;
    overflow: visible;
    color: rgba(46, 46, 46, 0.38);
    font-size: 10px;
    font-variant-numeric: tabular-nums;
    line-height: 14px;
    white-space: normal;
    overflow-wrap: anywhere;
  }
}

.directory-heading {
  display: flex;
  align-items: center;
  min-width: 0;
  gap: 6px;

  strong {
    flex: 1;
    min-width: 0;
  }
}

.directory-text .directory-code {
  display: inline-flex;
  align-items: center;
  width: max-content;
  max-width: 100%;
  flex-shrink: 0;
  gap: 4px;
  color: rgba(58, 104, 255, 1);
  font-size: 10px;
  font-weight: 500;
  line-height: 14px;
  text-overflow: clip;
  white-space: normal;
  overflow-wrap: anywhere;

  .directory-code-dot {
    display: inline-block;
    width: 5px;
    height: 5px;
    flex-shrink: 0;
    margin-top: 1px;
    border-radius: 50%;
    background: currentColor;
  }

  .directory-code-text {
    display: block;
    min-width: 0;
    line-height: 14px;
    white-space: nowrap;
  }

  &.cat-safety {
    color: rgba(255, 30, 30, 1);
  }

  &.cat-income {
    color: rgba(245, 158, 11, 1);
  }

  &.cat-normal {
    color: rgba(58, 104, 255, 1);
  }
}

.phase-dot {
  width: 6px;
  height: 6px;
  flex-shrink: 0;
  border-radius: 50%;
  background: currentColor;
}

.phase-status-enter-active,
.phase-status-leave-active {
  transition: opacity 0.16s ease, transform 0.16s ease;
}

.phase-status-enter-from {
  opacity: 0;
  transform: translateY(3px);
}

.phase-status-leave-to {
  opacity: 0;
  transform: translateY(-3px);
}

@keyframes phase-dot-pulse {

  0%,
  100% {
    opacity: 0.45;
    transform: scale(0.82);
  }

  50% {
    opacity: 1;
    transform: scale(1);
  }
}

@media (prefers-reduced-motion: reduce) {

  .phase-status-enter-active,
  .phase-status-leave-active {
    transition: none;
  }

  .phase-label .phase-dot {
    animation: none !important;
  }
}

.diagnosis-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.diagnosis-card {
  position: relative;
  scroll-margin-top: 16px;
  overflow: hidden;
  border: 1px solid rgba(31, 42, 68, 0.08);
  border-radius: 8px;
  background: #fff;
  transition: border-color 0.2s, box-shadow 0.2s;

  &::before {
    position: absolute;
    top: 13px;
    left: 0;
    width: 2px;
    height: 32px;
    border-radius: 0 999px 999px 0;
    background: rgba(58, 104, 255, 0.85);
    content: '';
  }

  &.cat-safety::before {
    background: rgba(255, 30, 30, 1);
  }

  &.cat-income::before {
    background: rgba(245, 158, 11, 1);
  }

  &.cat-normal::before {
    background: rgba(58, 104, 255, 1);
  }

  &:hover {
    border-color: rgba(58, 104, 255, 0.16);
    box-shadow: 0 5px 18px rgba(31, 42, 68, 0.05);
  }

  &:not(.is-collapsed) .diagnosis-card-header {
    border-bottom-color: rgba(0, 0, 0, 0.06);
  }

  h6 {
    margin: 0 0 8px;
    color: rgba(46, 46, 46, 0.72);
    font-size: 13px;
  }

  ul,
  ol {
    margin: 0;
    padding-left: 20px;
  }

  li,
  p {
    margin: 0 0 5px;
    color: rgba(46, 46, 46, 0.78);
    font-size: 13px;
    line-height: 1.7;
  }
}

.diagnosis-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  min-height: 58px;
  gap: 16px;
  padding: 11px 14px 11px 18px;
  border: 0;
  border-bottom: 1px solid transparent;
  background: transparent;
  color: inherit;
  font-family: inherit;
  text-align: left;
  cursor: pointer;
  transition: background 0.2s, border-color 0.2s;

  &:hover {
    background: rgba(58, 104, 255, 0.02);
  }

  &:focus-visible {
    outline: 2px solid rgba(58, 104, 255, 0.45);
    outline-offset: -2px;
  }
}

.diagnosis-card-title {
  min-width: 0;

  h5 {
    margin: 0;
    overflow: hidden;
    color: #2e2e2e;
    font-size: 14px;
    font-weight: 600;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  span {
    display: block;
    margin-top: 3px;
    color: rgba(46, 46, 46, 0.42);
    font-size: 11px;
    font-variant-numeric: tabular-nums;
  }
}

.diagnosis-card-actions {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  gap: 10px;
}

.diagnosis-card-body {
  padding: 0 18px 2px;
}

.diagnosis-category-badge {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  gap: 4px;
  padding: 4px 9px;
  border-radius: 999px;
  background: rgba(58, 104, 255, 0.08);
  color: rgba(58, 104, 255, 1);
  font-size: 11px;
  font-weight: 600;

  &.cat-safety {
    background: rgba(255, 30, 30, 0.08);
    color: rgba(255, 30, 30, 1);
  }

  &.cat-income {
    background: rgba(245, 158, 11, 0.1);
    color: rgba(245, 158, 11, 1);
  }

  &.cat-normal {
    background: rgba(58, 104, 255, 0.08);
    color: rgba(58, 104, 255, 1);
  }
}

.diagnosis-flow {
  padding-top: 16px;
}

.diagnosis-flow-step {
  position: relative;
  display: grid;
  grid-template-columns: 24px minmax(0, 1fr);
  gap: 12px;

  &:not(:last-child)::before {
    position: absolute;
    top: 28px;
    bottom: 4px;
    left: 11px;
    width: 1px;
    background: rgba(58, 104, 255, 0.16);
    content: '';
  }
}

.flow-step-index {
  position: relative;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 6px;
  background: rgba(58, 104, 255, 0.08);
  color: #3A68FF;
  font-size: 12px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}

.flow-step-content {
  min-width: 0;
  padding: 1px 0 16px;

  h6 {
    margin-bottom: 8px;
    color: #2e2e2e;
    font-weight: 600;
  }
}

.diagnosis-flow-step:not(:last-child) .flow-step-content {
  margin-bottom: 14px;
  border-bottom: 0;
}

// 设备与版本证据
.device-evidence-list {
  padding-left: 0;
  list-style: none;

  > li {
    margin-bottom: 0;
  }
}

.device-evidence-item {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 28px;
  font-size: 13px;

  :deep(.v-icon) {
    flex-shrink: 0;
  }

  .device-evidence-name {
    flex-shrink: 0;
    font-weight: 500;
    color: #2e2e2e;
  }

  .device-evidence-uuid {
    min-width: 0;
    flex: 1;
    overflow: hidden;
    color: rgba(46, 46, 46, 0.42);
    font-family: 'SF Mono', 'Courier New', monospace;
    font-size: 11px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .device-evidence-version {
    flex-shrink: 0;
    padding: 1px 8px;
    border-radius: 4px;
    background: rgba(58, 104, 255, 0.08);
    color: #3A68FF;
    font-size: 11px;
    font-weight: 500;

    &.is-missing {
      background: rgba(46, 46, 46, 0.06);
      color: rgba(46, 46, 46, 0.45);
      font-weight: 400;
    }
  }
}

.evidence-tag {
  display: inline-block;
  margin: 3px 0 0 5px;
  padding: 1px 6px;
  border-radius: 4px;
  background: rgba(58, 104, 255, 0.07);
  color: #3A68FF;
  font-size: 10px;
}

.empty-inline {
  padding: 18px;
  color: rgba(46, 46, 46, 0.45);
  font-size: 13px;
  text-align: center;
}

@media (max-width: 900px) {
  .diagnosis-result-layout {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .diagnosis-directory {
    position: static;
    padding: 0 0 12px;
    overflow: hidden;
    border-right: 0;
    border-bottom: none;

    nav {
      flex-direction: row;
      overflow-x: auto;
      padding-bottom: 2px;
    }

    button {
      flex: 0 0 160px;
    }
  }
}
</style>
